import { useCallback, useEffect, useState } from 'react';

// useAnnouncements drives the "platform notifications" rows in the bell:
// release notes, downtime warnings, anything we want every user to see
// regardless of org. Replaces the older useNewVersionAvailable hook — the
// announcements feed is now the single source of truth.
//
// Source: /announcements.json (committed to the repo at
// frontend/public/announcements.json, served verbatim by the Go binary).
// Polled once a minute, paused while the tab is hidden, immediate recheck
// on visibility-restore.
//
// Read state lives in localStorage:
//   - `announcementsFirstSeen` — ISO timestamp set on first invocation.
//     We only surface entries created AFTER that timestamp so a brand-new
//     user doesn't get flooded with the historic backlog on first login.
//   - `announcementsSeenIds` — JSON array of ids the user has dismissed
//     (either by clicking the row or by "mark all as read").
//
// localStorage scope = per browser, which is the right granularity for
// "release notes" — losing the read state on a browser wipe is harmless,
// and users who switch devices get re-notified, which is fine.

const POLL_MS = 60_000;
const FEED_URL = '/announcements.json';
const FIRST_SEEN_KEY = 'announcementsFirstSeen';
const SEEN_IDS_KEY = 'announcementsSeenIds';

export type AnnouncementSeverity = 'info' | 'success' | 'warning' | 'error';

export interface Announcement {
  id: string;
  severity: AnnouncementSeverity;
  created_at: string;
  action_url?: string;
}

interface FeedPayload {
  announcements?: Announcement[];
}

export interface UseAnnouncementsResult {
  unseen: Announcement[];
  markSeen: (id: string) => void;
  markAllSeen: () => void;
}

function readSeenIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(SEEN_IDS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function writeSeenIds(ids: Set<string>) {
  try {
    window.localStorage.setItem(SEEN_IDS_KEY, JSON.stringify([...ids]));
  } catch {
    // Quota or disabled storage — degrade silently. The worst case is the
    // user sees a dismissed announcement again on next load, which is mildly
    // annoying but not broken.
  }
}

function readOrInitFirstSeen(): string {
  try {
    const existing = window.localStorage.getItem(FIRST_SEEN_KEY);
    if (existing) return existing;
    const now = new Date().toISOString();
    window.localStorage.setItem(FIRST_SEEN_KEY, now);
    return now;
  } catch {
    // Storage disabled — fall back to "now" without persisting. Means every
    // session is treated as first-visit and historic announcements stay
    // suppressed.
    return new Date().toISOString();
  }
}

export function useAnnouncements(): UseAnnouncementsResult {
  const [all, setAll] = useState<Announcement[]>([]);
  const [seenIds, setSeenIds] = useState<Set<string>>(() => readSeenIds());
  const [firstSeen] = useState<string>(() => readOrInitFirstSeen());

  useEffect(() => {
    let cancelled = false;

    async function fetchFeed() {
      if (document.hidden) return;
      try {
        const res = await fetch(FEED_URL, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const data = (await res.json()) as FeedPayload;
        if (cancelled) return;
        const items = Array.isArray(data.announcements) ? data.announcements : [];
        setAll(items);
      } catch {
        // Network blip or HTML fallback — ignore, retry on next tick.
      }
    }

    void fetchFeed();
    const id = window.setInterval(() => void fetchFeed(), POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void fetchFeed();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const markSeen = useCallback((id: string) => {
    setSeenIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      writeSeenIds(next);
      return next;
    });
  }, []);

  const markAllSeen = useCallback(() => {
    setSeenIds((prev) => {
      const next = new Set(prev);
      for (const a of all) next.add(a.id);
      writeSeenIds(next);
      return next;
    });
  }, [all]);

  const unseen = all
    .filter((a) => a.created_at > firstSeen && !seenIds.has(a.id))
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

  return { unseen, markSeen, markAllSeen };
}
